import {
  Button as MuiButton,
  Link as MuiLink,
  LinkProps as MuiLinkProps,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import GaTracker from '../../utils/GaTracker/GaTracker';
import { buttonStyles, sizeStyles } from '../button/Button';

type Variant = 'outlined' | 'contained' | 'text';
type Size = 'large' | 'medium' | 'small';

export default function ButtonLink({
  variant = 'contained',
  children,
  fullWidth,
  external,
  label,
  to,
  onClick,
  size = 'medium',
  sx = {},
  action,
  target,
}: {
  variant?: Variant;
  onClick?: () => void;
  fullWidth?: boolean;
  label?: string;
  to: string;
  external?: boolean;
  children: ReactNode;
  size?: Size;
  sx?: SxProps<Theme>;
  action: string;
  target?: MuiLinkProps['target'];
}) {
  const history = useHistory();

  const gaAction = useMemo(() => {
    const pathname = history?.location?.pathname;

    if (external) {
      return `convert-to-${action}`;
    } else if (typeof pathname === 'string' && typeof to === 'string') {
      return `navigate-from${
        pathname === '/' ? '-home' : pathname.replace('/', '-')
      }-to${to.replace('/', '-')}`;
    } else if (typeof pathname === 'string') {
      return `navigate-from${pathname === '/' ? '-home' : pathname.replace('/', '-')}`;
    } else if (typeof to === 'string') {
      return `navigate-to${to.replace('/', '-')}`;
    } else {
      return `navigate-${action}`;
    }
  }, [to, history?.location?.pathname, action, external]);

  const style: SxProps<Theme> = Object.assign({}, buttonStyles[variant], sizeStyles[size], sx);

  return (
    <MuiButton
      component={external ? MuiLink : RouterLink}
      {...(external
        ? {
            rel: 'noopener noreferrer',
            target: target || '_blank',
            to: { pathname: to },
          }
        : {
            to: to,
          })}
      fullWidth={fullWidth}
      size={size}
      title={label}
      href={to}
      onClick={() => {
        GaTracker.trackEvent({
          category: 'Link',
          action: gaAction,
          label: to,
        });
        onClick?.();
      }}
      sx={style}
    >
      {children}
    </MuiButton>
  );
}
